/**
 * Project Service
 * Handles API calls to the backend for Autodesk Construction projects
 */

import { getProjectChangeOrders } from './changeOrderService'
import { withCache, invalidateCache } from '../utils/apiCache'

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3003'
const PROJECT_API = `${API_BASE_URL}/api/projects`
const FIELD_REPORTS_API = `${API_BASE_URL}/api/field-reports`
const CONFIG_API = `${API_BASE_URL}/api/config`

/**
 * Get application configuration (project ID, template IDs)
 * @returns {Promise<object>} {success, config: {projectId, fuelLogTemplateId}}
 */
export const getAppConfig = async () => {

  const response = await fetch(CONFIG_API, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
    },
  })


  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Unknown error' }))
    throw new Error(error.error || `Failed to fetch configuration (${response.status})`)
  }

  const data = await response.json()
  return data
}

/**
 * Get access token from localStorage
 * @returns {string|null} Access token
 */
const getAccessToken = () => {
  return localStorage.getItem('aps_access_token')
}

/**
 * Get all users in a project
 * @param {string} projectId - Project ID
 * @param {number} limit - Number of users to fetch (default: 200)
 * @returns {Promise<object>} {success, projectId, users, pagination}
 */
export const getProjectUsers = async (projectId, limit = 200) => {
  const token = getAccessToken()


  if (!token) {
    throw new Error('No access token available. Please log in.')
  }

  const headers = {
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${token}`,
  }


  const response = await fetch(`${PROJECT_API}/${projectId}/users?limit=${limit}`, {
    method: 'GET',
    headers,
  })


  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Unknown error' }))
    throw new Error(error.error || `Failed to fetch project users (${response.status})`)
  }

  const data = await response.json()
  return data
}

/**
 * Get project details
 * @param {string} projectId - Project ID
 * @returns {Promise<object>} {success, project}
 */
export const getProjectDetails = async (projectId) => {
  const token = getAccessToken()

  if (!token) {
    throw new Error('No access token available. Please log in.')
  }

  const response = await fetch(`${PROJECT_API}/${projectId}`, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
    },
  })

  if (!response.ok) {
    const error = await response.json()
    throw new Error(error.error || 'Failed to fetch project details')
  }

  return response.json()
}

/**
 * Get all forms in a project from ACC
 * @param {string} projectId - Project ID
 * @param {number} limit - Number of forms to fetch (default: 50, max: 50)
 * @param {number} offset - Offset for pagination (default: 0)
 * @param {string} templateId - Optional form template ID to filter by
 * @returns {Promise<object>} {success, projectId, forms, pagination}
 */
export const getProjectForms = async (projectId, limit = 50, offset = 0, templateId = null) => {
  const token = getAccessToken()

  if (templateId) {
  }

  if (!token) {
    throw new Error('No access token available. Please log in.')
  }

  let url = `${PROJECT_API}/${projectId}/forms?limit=${limit}&offset=${offset}`
  if (templateId) {
    url += `&templateId=${encodeURIComponent(templateId)}`
  }

  const response = await fetch(url, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
    },
  })


  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Unknown error' }))
    throw new Error(error.error || `Failed to fetch project forms (${response.status})`)
  }

  const data = await response.json()
  return data
}

/**
 * Create a new form from a template
 * @param {string} projectId - Project ID
 * @param {string} templateId - Form template ID
 * @returns {Promise<object>} {success, projectId, templateId, form, accFormUrl}
 */
export const createForm = async (projectId, templateId) => {
  const token = getAccessToken()


  if (!token) {
    throw new Error('No access token available. Please log in.')
  }

  const response = await fetch(`${PROJECT_API}/${projectId}/form-templates/${templateId}/forms`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
      'Accept': 'application/json',
    },
    body: JSON.stringify({}),
  })


  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Unknown error' }))
    throw new Error(error.error || `Failed to create form (${response.status})`)
  }

  const data = await response.json()
  return data
}

/**
 * Get form review chain (workflow/approval chain)
 * @param {string} projectId - Project ID
 * @param {string} formId - Form ID
 * @returns {Promise<object>} {success, projectId, formId, reviewChain}
 */
export const getFormReviewChain = async (projectId, formId) => {
  const token = getAccessToken()


  if (!token) {
    throw new Error('No access token available. Please log in.')
  }

  const response = await fetch(`${PROJECT_API}/${projectId}/forms/${formId}/review-chain`, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
    },
  })


  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Unknown error' }))
    throw new Error(error.error || `Failed to fetch review chain (${response.status})`)
  }

  const data = await response.json()
  return data
}

/**
 * Get field report review chain (workflow/approval chain)
 * @param {string} projectId - Project ID
 * @param {string} reportId - Report ID
 * @returns {Promise<object>} {success, projectId, reportId, reviewChain}
 */
export const getFieldReportReviewChain = async (projectId, reportId) => {
  const token = getAccessToken()

  if (!token) {
    throw new Error('No access token available. Please log in.')
  }

  const response = await fetch(`${FIELD_REPORTS_API}/${projectId}/${reportId}/review-chain`, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
    },
  })

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Unknown error' }))
    throw new Error(error.error || `Failed to fetch field report review chain (${response.status})`)
  }

  const data = await response.json()
  return data
}

/**
 * Get specific form details by form ID
 * @param {string} projectId - Project ID
 * @param {string} formId - Form ID
 * @returns {Promise<object>} {success, projectId, formId, form}
 */
export const getFormDetails = async (projectId, formId) => {
  const token = getAccessToken()


  if (!token) {
    throw new Error('No access token available. Please log in.')
  }

  const response = await fetch(`${PROJECT_API}/${projectId}/forms/${formId}`, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
    },
  })


  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Unknown error' }))
    throw new Error(error.error || `Failed to fetch form details (${response.status})`)
  }

  const data = await response.json()
  return data
}

export const getFieldReportTemplate = async (projectId, templateId) => {
  const token = getAccessToken()

  if (!token) {
    throw new Error('No access token available. Please log in.')
  }

  const resp = await fetch(
    `${FIELD_REPORTS_API}/${projectId}/templates/${templateId}/fields`,
    {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
    }
  );

  if (!resp.ok) {
    const error = await resp.json().catch(() => ({ error: 'Unknown error' }))
    throw new Error(error.error || `Failed to fetch template fields (${resp.status})`)
  }

  return resp.json();
};

/**
 * Create a new field report (fuel log) from a template
 * @param {string} projectId - Project ID
 * @param {string} templateId - Field report template ID
 * @returns {Promise<object>} {success, projectId, templateId, reportId, report, accUrl}
 */
export const createFieldReport = async (projectId, templateId) => {
  const token = getAccessToken()


  if (!token) {
    throw new Error('No access token available. Please log in.')
  }

  const response = await fetch(`${FIELD_REPORTS_API}/${projectId}/templates/${templateId}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
      'Accept': 'application/json',
    },
    body: JSON.stringify({}),
  })


  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Unknown error' }))
    throw new Error(error.error || `Failed to create field report (${response.status})`)
  }

  const data = await response.json()
  return data
}

/**
 * Get all field reports in a project
 * @param {string} projectId - Project ID
 * @param {number} limit - Number of reports to fetch (default: 50, max: 50)
 * @param {number} offset - Offset for pagination (default: 0)
 * @param {string} templateId - Optional template ID to filter by
 * @param {string} status - Optional status to filter by (e.g., 'inReview', 'closed', 'inProgress')
 * @returns {Promise<object>} {success, projectId, reports, pagination}
 */
// Internal function without caching
const _getFieldReportsUncached = async (projectId, limit = 50, offset = 0, templateId = null, status = null) => {
  const token = getAccessToken()


  if (!token) {
    throw new Error('No access token available. Please log in.')
  }

  let url = `${FIELD_REPORTS_API}/${projectId}?limit=${limit}&offset=${offset}`
  if (templateId) {
    url += `&templateId=${encodeURIComponent(templateId)}`
  }
  if (status) {
    url += `&status=${encodeURIComponent(status)}`
  }

  const response = await fetch(url, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
    },
  })


  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Unknown error' }))
    throw new Error(error.error || `Failed to fetch field reports (${response.status})`)
  }

  const data = await response.json()
  return data
}

/**
 * Get field reports with caching (5 min cache)
 * Cached wrapper around field reports API
 * @param {string} projectId - Project ID
 * @param {number} limit - Max number of reports to return (default: 20 for better performance)
 * @param {number} offset - Offset for pagination (default: 0)
 * @param {string} templateId - Optional template ID to filter by
 * @param {string} status - Optional status to filter by (e.g., 'inReview', 'closed', 'inProgress')
 * @returns {Promise<object>} {success, projectId, reports, pagination}
 */
export const getFieldReports = async (projectId, limit = 20, offset = 0, templateId = null, status = null) => {
  // Build cache key from parameters
  const cacheKey = `field-reports:${projectId}:${templateId || 'all'}:${status || 'all'}:${limit}:${offset}`

  return withCache(cacheKey, () =>
    _getFieldReportsUncached(projectId, limit, offset, templateId, status)
  )
}

/**
 * Invalidate field reports cache for a project
 * Call this after approving/rejecting/editing forms
 * @param {string} projectId - Project ID to invalidate
 */
export const invalidateFieldReportsCache = (projectId) => {
  invalidateCache(`field-reports:${projectId}`)
}

/**
 * Get field report details including form fields
 * @param {string} projectId - Project ID
 * @param {string} reportId - Field report ID
 * @returns {Promise<object>} {success, projectId, reportId, report, accUrl}
 */
export const getFieldReportDetails = async (projectId, reportId) => {
  const token = getAccessToken()


  if (!token) {
    throw new Error('No access token available. Please log in.')
  }

  const response = await fetch(`${FIELD_REPORTS_API}/${projectId}/${reportId}`, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
    },
  })


  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Unknown error' }))
    throw new Error(error.error || `Failed to fetch field report details (${response.status})`)
  }

  const data = await response.json()
  return data
}

/**
 * Update field report with form data
 * @param {string} projectId - Project ID
 * @param {string} reportId - Field report ID
 * @param {object} formData - Form field values to update
 * @returns {Promise<object>} {success, projectId, reportId, report}
 */
export const updateFieldReport = async (projectId, reportId, formData) => {
  const token = getAccessToken()


  if (!token) {
    throw new Error('No access token available. Please log in.')
  }

  // Build URL with templateId query param if provided
  let url = `${FIELD_REPORTS_API}/${projectId}/${reportId}`
  if (formData.templateId) {
    url += `?templateId=${formData.templateId}`
  }

  const response = await fetch(url, {
    method: 'PATCH',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
    },
    body: JSON.stringify(formData),
  })


  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Unknown error' }))
    throw new Error(error.error || `Failed to update field report (${response.status})`)
  }

  const data = await response.json()
  return data
}

/**
 * Submit field report for review
 * @param {string} projectId - Project ID
 * @param {string} reportId - Report ID
 * @param {string} templateId - Template ID
 * @returns {Promise<object>} {success, projectId, reportId, report, message}
 */
export const submitFieldReportForReview = async (projectId, reportId, templateId) => {
  const token = getAccessToken()


  if (!token) {
    throw new Error('No access token available. Please log in.')
  }

  const url = templateId
    ? `${FIELD_REPORTS_API}/${projectId}/${reportId}/submit?templateId=${templateId}`
    : `${FIELD_REPORTS_API}/${projectId}/${reportId}/submit`

  const response = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
    },
  })


  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Unknown error' }))
    throw new Error(error.error || `Failed to submit field report (${response.status})`)
  }

  const data = await response.json()
  return data
}

/**
 * Get project locations for dropdown
 * @param {string} projectId - Project ID
 * @returns {Promise<object>} {success, projectId, locations, pagination}
 */
export const getProjectLocations = async (projectId) => {
  const token = getAccessToken()


  if (!token) {
    throw new Error('No access token available. Please log in.')
  }

  const response = await fetch(`${FIELD_REPORTS_API}/${projectId}/locations`, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
    },
  })


  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Unknown error' }))
    throw new Error(error.error || `Failed to fetch locations (${response.status})`)
  }

  const data = await response.json()
  return data
}

/**
 * Detect if user is on a mobile device
 * @returns {boolean} true if mobile
 */
const isMobileDevice = () => {
  return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent)
}

/**
 * Open ACC URL with mobile app deep link support
 * Attempts to open in ACC mobile app first, falls back to browser
 * @param {string} accUrl - ACC web URL
 */
const openACCUrl = (accUrl) => {
  if (!accUrl) {
    console.warn('openACCUrl: No URL provided')
    return
  }

  const isMobile = isMobileDevice()

  if (isMobile) {
    // Extract form ID and project ID from ACC URL if possible
    // ACC URL format: https://acc.autodesk.com/docs/files/projects/{projectId}/forms/{formId}
    const urlMatch = accUrl.match(/projects\/([^\/]+)\/forms\/([^\/]+)/)

    if (urlMatch) {
      const [, projectId, formId] = urlMatch

      // Try ACC mobile app deep link
      // ACC mobile app uses custom URL scheme: autodesk360://
      const deepLink = `autodesk360://project/${projectId}/form/${formId}`


      // Attempt to open app
      window.location.href = deepLink

      // Fallback to web version after a short delay if app doesn't open
      setTimeout(() => {
        window.location.href = accUrl // Same window on mobile for better UX
      }, 2000)
    } else {
      // If we can't parse the URL, just open in mobile browser
      window.location.href = accUrl
    }
  } else {
    // Desktop: open in new tab as before
    window.open(accUrl, '_blank')
  }
}

/**
 * Create a new field report and open it (ACC app on mobile, web on desktop)
 * @param {string} projectId
 * @param {string} templateId
 * @param {object} formData
 * @returns {Promise<object>} returns the createFieldReport response
 */
export const createFieldReportAndOpen = async (projectId, templateId, formData = {}) => {
  const result = await createFieldReport(projectId, templateId, formData)

  if (result && result.accUrl) {
    // Open with mobile app support
    openACCUrl(result.accUrl)
  } else {
    // fallback: return result for debugging if accUrl missing
    console.warn('createFieldReportAndOpen: accUrl missing in create response', result)
  }

  return result
}

/**
 * Approve a field report with custom fields and signature
 *
 * NEW ENDPOINT - Use this for approval workflow!
 * This endpoint:
 * - Updates approval tracking custom fields (if field IDs provided)
 * - Attaches signature to the form
 * - Works WITHOUT review workflow configuration
 * - Supports templates with or without approval tracking fields
 *
 * @param {string} projectId - Project ID
 * @param {string} reportId - Report ID
 * @param {string} templateId - Template ID
 * @param {object} options - Approval options
 * @param {string} options.signature - Base64 SVG signature (required)
 * @param {string} options.approvalDate - ISO date string (optional, uses current date if not provided)
 * @param {string} options.approvedRejectedOnFieldId - Optional field ID for approval date (only if template has this field)
 * @param {string} options.approvedRejectedByFieldId - Optional field ID for approver name (only if template has this field, name comes from authenticated user)
 * @param {string} options.approvalStatusFieldId - Optional field ID for "Approval status" radio button (only if template has this field)
 * @returns {Promise<object>} {success, formId, projectId, approvalMetadata}
 */
export async function approveFieldReport(
  projectId,
  reportId,
  templateId,
  {
    signature,
    approvalDate,
    approvedRejectedOnFieldId, // No default - only send if explicitly provided
    approvedRejectedByFieldId, // No default - only send if explicitly provided
    approvalStatusFieldId // Optional field for "Approval status" radio button
  } = {}
) {
  if (!projectId || !reportId || !templateId) {
    throw new Error('projectId, reportId and templateId are required')
  }

  if (!signature) {
    throw new Error('signature is required for approval')
  }

  // Get token from localStorage
  const token = getAccessToken()
  if (!token) {
    throw new Error('No access token available. Please log in.')
  }

  // Normalize signature to base64 (remove data URL prefix if present)
  let base64Signature = signature
  if (signature.startsWith('data:')) {
    base64Signature = signature.split(',')[1]
  }

  // Build request URL
  const url = `${FIELD_REPORTS_API}/${encodeURIComponent(projectId)}/${encodeURIComponent(reportId)}/approve-with-fields`

  // Build request body
  const body = {
    templateId,
    signature: base64Signature,
    approvalFields: {}
  }

  // Only include approval tracking fields if explicitly provided
  if (approvedRejectedOnFieldId) {
    body.approvalFields.approvedRejectedOnFieldId = approvedRejectedOnFieldId
  }

  if (approvedRejectedByFieldId) {
    body.approvalFields.approvedRejectedByFieldId = approvedRejectedByFieldId
  }

  // Add approval status field if provided
  if (approvalStatusFieldId) {
    body.approvalFields.approvalStatusFieldId = approvalStatusFieldId
  }

  // Note: approverName is now handled by backend from req.user
  // Do not send approverName from frontend
  if (approvalDate) {
    body.approvalFields.approvalDate = approvalDate
  }

  const resp = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    },
    body: JSON.stringify(body)
  })

  const json = await resp.json()

  if (!resp.ok) {
    const errorMessage = json.message || json.error || `Failed to approve field report (${resp.status})`
    const err = new Error(errorMessage)
    err.status = resp.status
    err.serverResponse = json
    throw err
  }

  // Invalidate cache after successful approval
  invalidateFieldReportsCache(projectId)

  return json
}

/**
 * Bulk approve multiple field reports with custom fields and signature
 *
 * This endpoint:
 * - Supports templates with or without approval tracking fields
 * - Only updates approval tracking fields if field IDs are provided
 *
 * @param {string} projectId - Project ID
 * @param {string[]} formIds - Array of form IDs to approve
 * @param {string} templateId - Template ID
 * @param {Object} options - Approval options
 * @param {string} options.signature - Base64 signature (with or without data: prefix)
 * @param {string} options.approvalDate - ISO date string (optional, defaults to now)
 * @param {string} options.approvedRejectedOnFieldId - Optional field ID for approval date (only if template has this field)
 * @param {string} options.approvedRejectedByFieldId - Optional field ID for approver name (only if template has this field, name comes from authenticated user)
 * @param {string} options.approvalStatusFieldId - Optional field ID for "Approval status" radio button (only if template has this field)
 * @returns {Promise<Object>} Bulk approval result with success/failure counts
 */
export async function bulkApproveFieldReports(
  projectId,
  formIds,
  templateId,
  {
    signature,
    approvalDate,
    approvedRejectedOnFieldId, // No default - only send if explicitly provided
    approvedRejectedByFieldId, // No default - only send if explicitly provided
    approvalStatusFieldId // Optional field for "Approval status" radio button
  } = {}
) {
  const token = getAccessToken()

  if (!projectId || !formIds || formIds.length === 0 || !templateId) {
    throw new Error('projectId, formIds (non-empty array), and templateId are required')
  }

  // Normalize signature to base64
  let base64Signature = signature
  if (signature && signature.startsWith('data:')) {
    base64Signature = signature.split(',')[1]
  }

  const url = `${FIELD_REPORTS_API}/${encodeURIComponent(projectId)}/bulk-approve-with-fields`

  const body = {
    formIds,
    templateId,
    signature: base64Signature,
    approvalFields: {}
  }

  // Only include approval tracking fields if explicitly provided
  if (approvedRejectedOnFieldId) {
    body.approvalFields.approvedRejectedOnFieldId = approvedRejectedOnFieldId
  }

  if (approvedRejectedByFieldId) {
    body.approvalFields.approvedRejectedByFieldId = approvedRejectedByFieldId
  }

  // Add approval status field if provided
  if (approvalStatusFieldId) {
    body.approvalFields.approvalStatusFieldId = approvalStatusFieldId
  }

  // Note: approverName is now handled by backend from req.user
  // Do not send approverName from frontend
  if (approvalDate) {
    body.approvalDate = approvalDate
  }

  const resp = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    },
    body: JSON.stringify(body)
  })

  const json = await resp.json()
  if (!resp.ok) {
    const err = new Error(json.message || `Bulk approval failed (${resp.status})`)
    err.status = resp.status
    err.serverResponse = json
    throw err
  }

  // Invalidate cache after successful bulk approval
  invalidateFieldReportsCache(projectId)

  return json
}

/**
 * Reject a field report with reason
 *
 * This endpoint:
 * - Updates "Reason if Rejected" custom field (if rejectionFieldId provided)
 * - Works WITHOUT review workflow configuration
 * - Supports templates with or without rejection tracking fields
 *
 * @param {string} projectId - Project ID
 * @param {string} reportId - Report ID
 * @param {string} templateId - Template ID
 * @param {object} options - Rejection options
 * @param {string} options.reason - Rejection reason text (required)
 * @param {string} options.rejectionFieldId - Optional field ID for rejection reason (only if template has this field)
 * @returns {Promise<object>} {success, formId, projectId, rejectionReason}
 */
export async function rejectFieldReport(
  projectId,
  reportId,
  templateId,
  {
    reason,
    rejectionFieldId // No default - only send if explicitly provided
  } = {}
) {
  if (!projectId || !reportId || !templateId) {
    throw new Error('projectId, reportId and templateId are required')
  }

  if (!reason) {
    throw new Error('Rejection reason is required')
  }

  // Get token from localStorage
  const token = getAccessToken()
  if (!token) {
    throw new Error('No access token available. Please log in.')
  }

  const url = `${FIELD_REPORTS_API}/${encodeURIComponent(projectId)}/${encodeURIComponent(reportId)}/reject-with-reason`

  const body = {
    reason,
    templateId
  }

  // Only include rejectionFieldId if explicitly provided
  if (rejectionFieldId) {
    body.rejectionFieldId = rejectionFieldId
  }

  const resp = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    },
    body: JSON.stringify(body)
  })

  const json = await resp.json()
  if (!resp.ok) {
    const err = new Error(json.message || `Rejection failed (${resp.status})`)
    err.status = resp.status
    err.serverResponse = json
    throw err
  }

  // Invalidate cache after successful rejection
  invalidateFieldReportsCache(projectId)

  return json
}

/**
 * Bulk reject multiple field reports with reason
 *
 * This endpoint:
 * - Supports templates with or without rejection tracking fields
 * - Only updates rejection reason field if rejectionFieldId is provided
 *
 * @param {string} projectId - Project ID
 * @param {string[]} formIds - Array of form IDs to reject
 * @param {string} templateId - Template ID
 * @param {object} options - Rejection options
 * @param {string} options.reason - Rejection reason text (required)
 * @param {string} options.rejectionFieldId - Optional field ID for rejection reason (only if template has this field)
 * @returns {Promise<object>} {success, successCount, failureCount, results, errors}
 */
export async function bulkRejectFieldReports(
  projectId,
  formIds,
  templateId,
  {
    reason,
    rejectionFieldId // No default - only send if explicitly provided
  } = {}
) {
  if (!projectId || !templateId) {
    throw new Error('projectId and templateId are required')
  }

  if (!formIds || !Array.isArray(formIds) || formIds.length === 0) {
    throw new Error('formIds array is required and cannot be empty')
  }

  if (!reason) {
    throw new Error('Rejection reason is required')
  }

  // Get token from localStorage
  const token = getAccessToken()
  if (!token) {
    throw new Error('No access token available. Please log in.')
  }

  const url = `${FIELD_REPORTS_API}/${encodeURIComponent(projectId)}/bulk-reject-with-reason`

  const body = {
    formIds,
    templateId,
    reason
  }

  // Only include rejectionFieldId if explicitly provided
  if (rejectionFieldId) {
    body.rejectionFieldId = rejectionFieldId
  }

  const resp = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    },
    body: JSON.stringify(body)
  })

  const json = await resp.json()
  if (!resp.ok) {
    const err = new Error(json.message || `Bulk rejection failed (${resp.status})`)
    err.status = resp.status
    err.serverResponse = json
    throw err
  }

  // Invalidate cache after successful bulk rejection
  invalidateFieldReportsCache(projectId)

  return json
}

/**
 * @deprecated Use approveFieldReport() instead
 *
 * OLD ENDPOINT - This requires review workflow configuration in ACC
 * Will return 424 error if template doesn't have review workflow
 *
 * Client helper to sign & approve a field report.
 * - signature may be a full data URL (data:image/png;base64,...) OR raw base64 string.
 * - templateId is passed in query and as a fallback in the body.
 */
export async function signAndApprove(projectId, reportId, templateId, { signature, comment } = {}) {
  console.warn('⚠️  signAndApprove() is deprecated. Use approveFieldReport() instead.')
  console.warn('   This function requires review workflow configuration in ACC and may fail with 424 error.')

  if (!projectId || !reportId || !templateId) {
    throw new Error('projectId, reportId and templateId are required')
  }

  // normalize URL
  const url = `${FIELD_REPORTS_API}/${encodeURIComponent(projectId)}/${encodeURIComponent(reportId)}/sign-and-approve?templateId=${encodeURIComponent(templateId)}`

  // get token from localStorage (or use whichever storage you prefer)
  const token = localStorage.getItem('aps_access_token')

  // Build body
  const body = {}
  if (signature) {
    let dataUrl = signature
    // if user passed raw base64 (no "data:" prefix), normalize to PNG data URL
    if (typeof dataUrl === 'string' && !dataUrl.startsWith('data:')) {
      // strip any existing data: prefix just in case, then add png prefix
      dataUrl = `data:image/png;base64,${dataUrl.replace(/^data:image\/\w+;base64,/, '')}`
    }
    body.signature = dataUrl
  }
  if (comment) body.comment = comment

  const headers = { 'Content-Type': 'application/json' }
  if (token) headers['Authorization'] = `Bearer ${token}`

  const resp = await fetch(url, {
    method: 'POST',
    headers,
    body: Object.keys(body).length ? JSON.stringify(body) : JSON.stringify({})
  })

  const text = await resp.text().catch(() => '')
  let json = null
  try { json = text ? JSON.parse(text) : null } catch (e) { json = null }

  if (!resp.ok) {
    // helpful error with server payload (if any)
    const serverMessage = (json && (json.message || json.error || json.text)) || text || resp.statusText
    const err = new Error(serverMessage || `HTTP ${resp.status}`)
    err.status = resp.status
    err.serverResponse = json || text
    throw err
  }

  return json
}

/**
 * @deprecated Use form permissions system instead
 * This function is no longer needed as we now use template-level permissions
 * that check user-level, group-level, and project admin access
 *
 * @see useFormPermissions hook in src/hooks/useFormPermissions.js
 * @see formPermissionsService in src/services/formPermissionsService.js
 */
export const checkReviewerStatus = async (token) => {
  console.warn('⚠️  checkReviewerStatus() is deprecated. Use useFormPermissions hook or formPermissionsService instead.')
  console.warn('   The new permission system checks template-level permissions, groups, and project admin status.')

  // For backward compatibility, return reviewer status = true
  // Real permission checks should use the new form permissions system
  return {
    success: true,
    isReviewer: true,
    message: 'This endpoint is deprecated. Use form permissions system instead.'
  }
}

/**
 * Get all projects the user has access to
 * @returns {Promise<Array>} Array of projects with {id, name, type}
 */
export const getUserProjects = async () => {
  const token = getAccessToken()

  if (!token) {
    throw new Error('No access token available. Please log in.')
  }

  const response = await fetch(`${API_BASE_URL}/api/auth/user/projects`, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    }
  })

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Unknown error' }))
    throw new Error(error.error || `Failed to fetch user projects (${response.status})`)
  }

  const data = await response.json()
  return data.projects || []
}

/**
 * Get all form templates in a project
 * @param {string} projectId - Project ID
 * @returns {Promise<Array>} Array of templates with {id, name, type, category}
 */
export const getProjectTemplates = async (projectId) => {
  const token = getAccessToken()

  if (!token) {
    throw new Error('No access token available. Please log in.')
  }

  const response = await fetch(`${API_BASE_URL}/api/projects/${projectId}/form-templates`, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    }
  })

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Unknown error' }))
    throw new Error(error.error || `Failed to fetch project templates (${response.status})`)
  }

  const data = await response.json()
  return data.templates || []
}

/**
 * Get all forms across all templates in a project with status filter
 * @param {string} projectId - Project ID
 * @param {string} status - Status filter (inReview, inProgress, closed)
 * @param {number} limit - Number of forms to fetch
 * @returns {Promise<Array>} Array of forms
 */
export const getAllProjectForms = async (projectId, status = null, limit = 100) => {
  const token = getAccessToken()

  if (!token) {
    throw new Error('No access token available. Please log in.')
  }

  let url = `${API_BASE_URL}/api/projects/${projectId}/all-forms?limit=${limit}`
  if (status) {
    url += `&status=${status}`
  }

  const response = await fetch(url, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    }
  })

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Unknown error' }))
    throw new Error(error.error || `Failed to fetch all project forms (${response.status})`)
  }

  const data = await response.json()
  return data.forms || []
}

/**
 * Get forms from all projects the user has access to
 * @param {string|null} status - Optional status filter (inReview, inProgress, closed)
 * @param {number} limit - Maximum number of forms to return
 * @returns {Promise<Array>} Array of forms from all projects
 */
export const getFormsFromAllProjects = async (status = null, limit = 100) => {
  const token = getAccessToken()

  if (!token) {
    throw new Error('No access token available. Please log in.')
  }

  let url = `${API_BASE_URL}/api/projects/all-projects/all-forms?limit=${limit}`
  if (status) {
    url += `&status=${status}`
  }

  const response = await fetch(url, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    }
  })

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Unknown error' }))
    throw new Error(error.error || `Failed to fetch forms from all projects (${response.status})`)
  }

  const data = await response.json()
  return data.forms || []
}

/**
 * Get all contracts for a project
 * @param {string} projectId - Project ID
 * @param {string} status - Filter by status (optional)
 * @param {number} limit - Number of contracts to fetch
 * @returns {Promise<Array>} Array of contracts
 */
export const getProjectContracts = async (projectId, status = null, limit = 100) => {
  const token = getAccessToken()

  if (!token) {
    throw new Error('No access token available. Please log in.')
  }

  let url = `${API_BASE_URL}/api/cost/${projectId}/contracts?limit=${limit}`
  if (status) {
    url += `&status=${status}`
  }

  const response = await fetch(url, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    }
  })

  if (!response.ok) {
    // For 403 errors (permission denied), return empty array instead of throwing
    // This is expected for projects where user doesn't have Cost Management access
    if (response.status === 403) {
      console.warn(`[projectService] No access to Cost Management for project ${projectId}, returning empty contracts list`)
      return []
    }

    const error = await response.json().catch(() => ({ error: 'Unknown error' }))
    throw new Error(error.error || `Failed to fetch contracts (${response.status})`)
  }

  const data = await response.json()
  return data.contracts || []
}

/**
 * Get all subcontracts for a project
 * @param {string} projectId - Project ID
 * @param {string} status - Filter by status (optional)
 * @param {number} limit - Number of subcontracts to fetch
 * @returns {Promise<Array>} Array of subcontracts
 */
export const getProjectSubcontracts = async (projectId, status = null, limit = 100) => {
  const token = getAccessToken()

  if (!token) {
    throw new Error('No access token available. Please log in.')
  }

  let url = `${API_BASE_URL}/api/cost/${projectId}/subcontracts?limit=${limit}`
  if (status) {
    url += `&status=${status}`
  }

  const response = await fetch(url, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    }
  })

  if (!response.ok) {
    // For 403 errors (permission denied), return empty array instead of throwing
    // This is expected for projects where user doesn't have Cost Management access
    if (response.status === 403) {
      console.warn(`[projectService] No access to Cost Management for project ${projectId}, returning empty subcontracts list`)
      return []
    }

    const error = await response.json().catch(() => ({ error: 'Unknown error' }))
    throw new Error(error.error || `Failed to fetch subcontracts (${response.status})`)
  }

  const data = await response.json()
  return data.subcontracts || []
}

/**
 * Get all assets for a project from the Autodesk Assets API
 * Returns assets and a pre-built assetMap keyed by equipment number (ca1) -> clientAssetId
 * @param {string} projectId - Project ID
 * @returns {Promise<object>} {success, projectId, assets, assetMap}
 */
export const getProjectAssets = async (projectId, formIds = []) => {
  const token = getAccessToken()

  if (!token) {
    throw new Error('No access token available. Please log in.')
  }

  // If formIds array is too large, batch the requests to avoid URL length limits
  const BATCH_SIZE = 30

  if (formIds.length > BATCH_SIZE) {

    // Split formIds into batches
    const batches = []
    for (let i = 0; i < formIds.length; i += BATCH_SIZE) {
      batches.push(formIds.slice(i, i + BATCH_SIZE))
    }

    // Execute all batches in parallel
    const batchPromises = batches.map(async (batch, index) => {
      const url = new URL(`${FIELD_REPORTS_API}/${projectId}/assets`)
      url.searchParams.set('formIds', batch.join(','))

      try {
        const response = await fetch(url.toString(), {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
          },
        })

        if (!response.ok) {
          console.warn(`[getProjectAssets] Batch ${index + 1}/${batches.length} failed: ${response.status}`)
          return null
        }

        return await response.json()
      } catch (error) {
        console.warn(`[getProjectAssets] Batch ${index + 1}/${batches.length} error:`, error.message)
        return null
      }
    })

    const batchResults = await Promise.all(batchPromises)

    // Merge results from all batches
    const mergedAssets = []
    const mergedAssetMap = {}
    const mergedFormAssetMap = {}

    for (const result of batchResults) {
      if (!result) continue

      if (result.assets) {
        mergedAssets.push(...result.assets)
      }

      if (result.assetMap) {
        Object.assign(mergedAssetMap, result.assetMap)
      }

      if (result.formAssetMap) {
        Object.assign(mergedFormAssetMap, result.formAssetMap)
      }
    }

    return {
      success: true,
      projectId,
      assets: mergedAssets,
      assetMap: mergedAssetMap,
      formAssetMap: mergedFormAssetMap
    }
  }

  // For small arrays, use single request
  const url = new URL(`${FIELD_REPORTS_API}/${projectId}/assets`)
  if (formIds.length > 0) {
    url.searchParams.set('formIds', formIds.join(','))
  }

  const response = await fetch(url.toString(), {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
    },
  })

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Unknown error' }))
    throw new Error(error.error || `Failed to fetch assets (${response.status})`)
  }

  const data = await response.json()
  return data
}

export default {
  getAppConfig,
  getProjectUsers,
  getProjectDetails,
  getProjectForms,
  createForm,
  getFormReviewChain,
  getFieldReportReviewChain,
  getFormDetails,
  createFieldReport,
  getFieldReports,
  getFieldReportDetails,
  updateFieldReport,
  submitFieldReportForReview,
  getProjectLocations,
  getFieldReportTemplate,
  createFieldReportAndOpen,
  approveFieldReport, // NEW - Use this for approval
  rejectFieldReport, // NEW - Use this for rejection
  bulkApproveFieldReports, // NEW - Use this for bulk approval
  bulkRejectFieldReports, // NEW - Use this for bulk rejection
  checkReviewerStatus, // DEPRECATED - Use form permissions system instead
  getUserProjects, // NEW - Get all projects user has access to
  getProjectTemplates, // NEW - Get all form templates in a project
  getAllProjectForms, // NEW - Get all forms across all templates
  getFormsFromAllProjects, // NEW - Get forms from all accessible projects
  getProjectContracts, // NEW - Get all contracts in a project
  getProjectSubcontracts, // NEW - Get all subcontracts in a project
  getProjectChangeOrders, // NEW - Get all change orders in a project
  getProjectAssets, // NEW - Get all assets in a project (for Equipment Number matching)
  signAndApprove // DEPRECATED - Will be removed in future version
}
